import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { formatDay } from '../lib/format';
import { Icon } from './Icon';
import { useConfirm } from './Confirm';

/**
 * MI-HOZZAFERES (MCP) - szemelyes tokenek (versenyaudit M6).
 *
 * A kolléga sajat maga ad hozzaferest egy MI-nek (Claude Code, Claude
 * Desktop): az pontosan azt latja es teheti, amit o. Az olvasas mindig benne
 * van; az iras es a kozvetlen uzenetek (DM) kulon dontes, alapbol KI.
 * A titok csak egyszer latszik - utana csak visszavonni lehet.
 */

interface PersonalToken {
  id: string;
  name: string;
  prefix: string;
  write: boolean;
  dms: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
}

const tokensKey = ['me', 'tokens'] as const;

export function McpAccessSection() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { data } = useQuery({
    queryKey: tokensKey,
    queryFn: () => apiFetch<{ tokens: PersonalToken[] }>('/api/me/tokens').then((r) => r.tokens),
  });
  const [name, setName] = useState('Claude');
  const [write, setWrite] = useState(false);
  const [dms, setDms] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      apiFetch<{ id: string; secret: string }>('/api/me/tokens', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), write, dms }),
      }),
    onSuccess: (result) => {
      setCreated(result.secret);
      setCopied(false);
      void queryClient.invalidateQueries({ queryKey: tokensKey });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => apiFetch(`/api/me/tokens/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: tokensKey }),
  });

  const live = (data ?? []).filter((t) => !t.revokedAt);
  const endpoint = `${window.location.origin}/mcp`;
  const command = created
    ? `claude mcp add --transport http avslack ${endpoint} --header "Authorization: Bearer ${created}"`
    : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="border-t border-border-soft pt-4">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold">
        <Icon name="link" size="sm" /> MI-hozzáférés (MCP)
      </h3>
      <p className="mt-1 text-xs text-ink-muted">
        Egy MI (pl. Claude) a nevedben olvashatja a csatornáidat, kereshet bennük, és ha engeded,
        üzenetet küldhet. Pontosan azt éri el, amit te – többet soha. Amit küld, „MI-n keresztül”
        jelölést kap.
      </p>

      {created && (
        <div className="mt-3 rounded-card border border-accent/40 bg-accent-soft p-3">
          <p className="text-xs font-semibold text-accent">
            Kész. Ez a parancs csak most látszik – másold ki és futtasd a gépeden:
          </p>
          <code className="mt-2 block break-all rounded-control bg-surface px-2 py-1.5 text-2xs">
            {command}
          </code>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => void copy()}
              className="rounded-control bg-accent px-3 py-1 text-xs font-semibold text-accent-ink"
            >
              {copied ? 'Kimásolva' : 'Másolás'}
            </button>
            <button
              type="button"
              onClick={() => setCreated(null)}
              className="rounded-control px-2 py-1 text-xs text-ink-muted hover:text-ink"
            >
              Bezárás
            </button>
          </div>
          <p className="mt-2 text-2xs text-ink-muted">
            Claude Desktopban: Beállítások → Csatolók → Egyéni csatoló, cím: {endpoint}, fejléc:
            Authorization: Bearer &lt;token&gt;.
          </p>
        </div>
      )}

      {live.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {live.map((token) => (
            <li
              key={token.id}
              className="flex items-center gap-2 rounded-control border border-border-soft px-3 py-2 text-xs"
            >
              <span className="font-medium">{token.name}</span>
              <span className="font-mono text-ink-faint">{token.prefix}…</span>
              <span className="text-ink-muted">
                olvasás{token.write ? ', írás' : ''}
                {token.dms ? ', DM' : ''}
              </span>
              <span className="ml-auto text-ink-faint">
                {token.lastUsedAt
                  ? `használva: ${formatDay(token.lastUsedAt)}`
                  : 'még nem használt'}
              </span>
              <button
                type="button"
                onClick={() =>
                  void confirm({
                    title: 'MI-hozzáférés visszavonása',
                    body: `A(z) „${token.name}” token azonnal megszűnik; az MI többé nem éri el az AVSlacket.`,
                    confirmLabel: 'Visszavonás',
                  }).then((ok) => ok && revoke.mutate(token.id))
                }
                className="rounded-control px-2 py-0.5 text-ink-muted hover:bg-surface-hover hover:text-danger"
              >
                Visszavonás
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 space-y-2 rounded-card border border-border-soft p-3">
        <label className="block text-xs font-medium text-ink-muted">
          Megnevezés
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            className="mt-1 w-full rounded-control border border-border-soft bg-surface px-3 py-1.5 text-sm outline-none focus:border-accent"
          />
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={write}
            onChange={(e) => setWrite(e.target.checked)}
            className="mt-1"
          />
          <span>
            Írhat is
            <span className="block text-2xs text-ink-muted">
              Üzenetet küldhet, reagálhat, emlékeztetőt állíthat be – a nevedben.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={dms}
            onChange={(e) => setDms(e.target.checked)}
            className="mt-1"
          />
          <span>
            A közvetlen üzeneteimet (DM) is elérheti
            <span className="block text-2xs text-ink-muted">
              Alapból nem: a DM-ekben gyakran ügyféladat és személyes beszélgetés van.
            </span>
          </span>
        </label>
        {create.isError && (
          <p role="alert" className="text-xs text-danger">
            {create.error instanceof Error ? create.error.message : 'Nem sikerült.'}
          </p>
        )}
        <button
          type="button"
          onClick={() => create.mutate()}
          disabled={!name.trim() || create.isPending}
          className="rounded-control bg-accent px-3 py-1.5 text-sm font-semibold text-accent-ink disabled:opacity-50"
        >
          {create.isPending ? 'Készül…' : 'Hozzáférés létrehozása'}
        </button>
      </div>
    </section>
  );
}
